import React, { useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  campaignsPage,
  preferencesPage,
  profilePages,
  profileTabPages,
  calendarPage,
  contentPage,
  blueprintsPage,
  compose,
  profileServicePages,
} from '~/routes';
import { filterProfilesByOrg } from '~/profile-sidebar/utils';
import PagesWithSidebar from '~/app-pages/components/PagesWithSidebar';
import ProfilePages from '~/app-pages/components/ProfilePages';
import Preferences from '~/preferences';

import { getLastVisitedPage } from '~/utils/page-visits';
import { QUEUE_PAGE } from '~/constants';
import { useAccount, usePageLoad } from '../../../accountContext';
import ComposePage from '../../../compose-page';

const AppPages = ({
  unfilteredProfiles,
  showCalendarAsHomepage,
  profileRouteLoaded,
}) => {
  // Get current selected org from account
  const { account, loading } = useAccount();
  const { initialPageLoad, updateInitialPageLoad } = usePageLoad();
  const selectedOrg = account?.currentOrganization?.id;

  const profiles = filterProfilesByOrg(unfilteredProfiles, {
    id: selectedOrg,
  });

  const hasProfiles = profiles.length > 0;

  const shouldRedirectToCalendar = !loading && !hasProfiles;

  useEffect(() => {
    updateInitialPageLoad();
  });

  const redirectToQueue = () => {
    const selectedProfileId = profiles[0].id;
    const newPath = profileTabPages.getRoute({
      profileId: selectedProfileId,
      tabId: 'queue',
    });
    return (
      <Route>
        <Redirect to={newPath} />
      </Route>
    );
  };

  const redirectToCalendar = () => {
    return (
      <Route>
        <Redirect to={calendarPage.route} />
      </Route>
    );
  };

  const homepageRedirect = () => {
    /**
     * - Always redirect to queue for old users
     * - Redirect to queue only if last visited page is the queue for new users
     */
    const lastVisitedPage = getLastVisitedPage();
    const queueIsLastVisitedPage =
      lastVisitedPage && lastVisitedPage === QUEUE_PAGE && !initialPageLoad;
    const shouldRedirectToQueue =
      !showCalendarAsHomepage || queueIsLastVisitedPage;

    return shouldRedirectToQueue ? redirectToQueue() : redirectToCalendar();
  };

  return (
    <Switch>
      <Route path={compose.route} component={ComposePage} />

      <Route path={preferencesPage.route} component={Preferences} />

      <Route path={campaignsPage.route} component={PagesWithSidebar} />
      <Route path={contentPage.route} component={PagesWithSidebar} />
      <Route path={blueprintsPage.route} component={PagesWithSidebar} />
      <Route path={calendarPage.route} component={PagesWithSidebar} />
      {shouldRedirectToCalendar && <Redirect to={calendarPage.route} />}

      <Route
        path={profilePages.route}
        render={(props) => (
          <ProfilePages
            profiles={profiles}
            profileRouteLoaded={profileRouteLoaded}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
      />
      <Route
        path={profileServicePages.route}
        render={(props) => (
          <ProfilePages
            profiles={profiles}
            profileRouteLoaded={profileRouteLoaded}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
      />
      {hasProfiles && homepageRedirect()}
    </Switch>
  );
};

AppPages.propTypes = {
  unfilteredProfiles: PropTypes.arrayOf(PropTypes.shape),
  profileRouteLoaded: PropTypes.func.isRequired,
  needsToSetCurrentOrg: PropTypes.bool,
  orgIdFromRoute: PropTypes.string,
  storeSelectedOrg: PropTypes.func.isRequired,
  showCalendarAsHomepage: PropTypes.bool,
};

AppPages.defaultProps = {
  unfilteredProfiles: [],
  needsToSetCurrentOrg: false,
  orgIdFromRoute: null,
  showCalendarAsHomepage: false,
};

export default AppPages;
